import type {
  MessageContentType,
  ParticipantAssociation,
  ParticipantKind,
  ScheduledMessageSummary,
  ScheduledMessageStatus,
  ThreadDetail,
  ThreadMessage,
  AgentTrace,
  ThreadWorkflowRunSummary,
} from "skedyul-ui/ui";

type AgentKindValue = "SYSTEM" | "APP" | "WORKSPACE" | "MODEL";
import type { ScheduledMessageSummaryPayload } from "skedyul-core/features/threads";
import type { RouterOutputs } from "@utils/api";

const AGENT_KIND_VALUES = new Set<AgentKindValue>([
  "SYSTEM",
  "APP",
  "WORKSPACE",
  "MODEL",
]);

function toAgentKindValue(
  value: string | null | undefined,
): AgentKindValue | null {
  if (!value) return null;
  return AGENT_KIND_VALUES.has(value as AgentKindValue)
    ? (value as AgentKindValue)
    : null;
}

function toMessageContentType(value: string): MessageContentType {
  if (
    value === "TEXT" ||
    value === "HTML" ||
    value === "MARKDOWN" ||
    value === "MEDIA" ||
    value === "SYSTEM"
  ) {
    return value;
  }
  return "TEXT";
}

function toParticipantKind(value: string): ParticipantKind {
  if (
    value === "CONTACT" ||
    value === "MEMBER" ||
    value === "AGENT" ||
    value === "SYSTEM" ||
    value === "WORKFLOW" ||
    value === "BATCH_JOB"
  ) {
    return value;
  }
  return "SYSTEM";
}

export function mapThreadMessageAttachment(a: {
  id: string;
  fileId?: string;
  name: string;
  mimeType: string | null;
  url: string;
}) {
  return {
    id: a.id,
    fileId: a.fileId,
    name: a.name,
    mimeType: a.mimeType,
    url: a.url,
  };
}

function toScheduledMessageStatus(value: string): ScheduledMessageStatus {
  if (value === "PENDING" || value === "SENT" || value === "CANCELLED") {
    return value;
  }
  return "PENDING";
}

export function mapScheduledMessagePayloadsToSummaries(
  messages: ScheduledMessageSummaryPayload[],
): ScheduledMessageSummary[] {
  return messages.map((message) => ({
    id: message.id,
    content: message.content,
    contentType: toMessageContentType(message.contentType),
    scheduledAt: message.scheduledAt,
    status: toScheduledMessageStatus(message.status),
    cancelOnActivity: message.cancelOnActivity,
    requiresApproval: message.requiresApproval,
    approvedAt: message.approvedAt,
    approvalBlockId: message.approvalBlockId,
    sender: {
      id: message.sender.id,
      displayName: message.sender.displayName ?? "Unknown",
      avatarUrl: message.sender.avatarUrl,
      kind: toParticipantKind(message.sender.kind),
    },
  }));
}

export type ThreadGetPayload = NonNullable<RouterOutputs["threads"]["get"]>;

export type ContactActivityFields = {
  lastContactedAt: string | null
  lastActivityAt: string | null
  lastMessage: string | null
}

/** Sidebar row shape (matches prior page-local ThreadListItem). */
export interface ThreadListRow {
  id: string;
  title: string | null;
  status: "OPEN" | "RESOLVED" | "ARCHIVED";
  kind: "CONVERSATION" | "INTERNAL" | "ESCALATION" | "BROADCAST" | "TESTING";
  lastMessageAt: string | null;
  lastMessagePreview: string | null;
  lastActivityAt?: string | null;
  lastActivityType?: string | null;
  unreadCount: number;
  pendingApprovalCount?: number;
  listIcon?: string | null;
  participants: Array<{
    id: string;
    displayName: string;
    avatarUrl: string | null;
    kind: "CONTACT" | "MEMBER" | "AGENT" | "SYSTEM" | "WORKFLOW" | "BATCH_JOB";
    role: "OWNER" | "PARTICIPANT" | "OBSERVER";
    joinedAt: string;
    memberId?: string | null;
    contactId?: string | null;
    agentId?: string | null;
    workflowId?: string | null;
    contactActivity?: ContactActivityFields | null;
    associations?: ParticipantAssociation[];
  }>;
  parentId?: string | null;
  subThreadCount?: number;
  routingParticipantId?: string | null;
  communicationChannelId?: string | null;
  campaignRecipient?: boolean;
  campaignThreadId?: string | null;
  isCampaignHub?: boolean;
  campaignListedAt?: string | null;
  campaignMessageCount?: number | null;
  routingPattern?: string | null;
  routingPatternDisplay?: string | null;
  routingAddress?: string | null;
  isExclusiveFeed?: boolean;
  // Status indicator fields for sidebar dots
  hasActiveWorkflow?: boolean;
  hasActiveAgent?: boolean;
  hasScheduledMessage?: boolean;
  nextScheduledAt?: string | null;
  pinnedAt?: string | null;
  viewerIsParticipant?: boolean;
}

export function mapListItems(
  items: RouterOutputs["threads"]["list"]["items"],
): ThreadListRow[] {
  return (items ?? []).map((t) => ({
    id: t.id,
    title: t.title,
    status: t.status as "OPEN" | "RESOLVED" | "ARCHIVED",
    kind: t.kind as
      | "CONVERSATION"
      | "INTERNAL"
      | "ESCALATION"
      | "BROADCAST"
      | "TESTING",
    lastMessageAt: t.lastMessageAt,
    lastMessagePreview: t.lastMessagePreview,
    lastActivityAt:
      (t as { lastActivityAt?: string | null }).lastActivityAt ?? null,
    lastActivityType:
      (t as { lastActivityType?: string | null }).lastActivityType ?? null,
    unreadCount: t.unreadCount,
    listIcon: t.listIcon ?? null,
    participants: t.participants.map((p) => ({
      id: p.id,
      displayName: p.displayName,
      avatarUrl: p.avatarUrl,
      kind: p.kind as
        | "CONTACT"
        | "MEMBER"
        | "AGENT"
        | "SYSTEM"
        | "WORKFLOW"
        | "BATCH_JOB",
      role: p.role as "OWNER" | "PARTICIPANT" | "OBSERVER",
      joinedAt: p.joinedAt,
      memberId: (p as { memberId?: string | null }).memberId ?? null,
      contactId: (p as { contactId?: string | null }).contactId ?? null,
      agentId: (p as { agentId?: string | null }).agentId ?? null,
      workflowId: (p as { workflowId?: string | null }).workflowId ?? null,
      contactActivity:
        (p as { contactActivity?: ContactActivityFields | null })
          .contactActivity ?? null,
      associations: (
        (
          p as unknown as {
            associations?: Array<{
              instanceId?: string | null;
              label?: string;
              modelHandlePlural?: string | null;
              modelName?: string | null;
              imageSeed?: string | null;
            }>;
          }
        ).associations ?? []
      ).flatMap((a): ParticipantAssociation[] => {
        if (!a.instanceId) return [];
        return [
          {
            instanceId: a.instanceId,
            label: a.label ?? "",
            modelHandlePlural: a.modelHandlePlural ?? "",
            modelName: a.modelName ?? "",
            ...(a.imageSeed ? { imageSeed: a.imageSeed } : {}),
          },
        ];
      }),
    })),
    parentId: t.parentId,
    subThreadCount: t.subThreadCount,
    routingParticipantId:
      (t as { routingParticipantId?: string | null }).routingParticipantId ??
      null,
    communicationChannelId:
      (t as { communicationChannelId?: string | null })
        .communicationChannelId ?? null,
    campaignRecipient:
      (t as { campaignRecipient?: boolean }).campaignRecipient ?? false,
    campaignThreadId:
      (t as { campaignThreadId?: string | null }).campaignThreadId ?? null,
    isCampaignHub: (t as { isCampaignHub?: boolean }).isCampaignHub ?? false,
    campaignListedAt:
      (t as { campaignListedAt?: string | null }).campaignListedAt ?? null,
    campaignMessageCount:
      (t as { campaignMessageCount?: number | null }).campaignMessageCount ??
      null,
    routingPattern:
      (t as { routingPattern?: string | null }).routingPattern ?? null,
    routingPatternDisplay:
      (t as { routingPatternDisplay?: string | null }).routingPatternDisplay ??
      null,
    routingAddress:
      (t as { routingAddress?: string | null }).routingAddress ?? null,
    isExclusiveFeed:
      (t as { isExclusiveFeed?: boolean }).isExclusiveFeed ?? false,
    // Status indicator fields
    pendingApprovalCount:
      (t as { pendingApprovalCount?: number }).pendingApprovalCount ?? 0,
    hasActiveWorkflow:
      (t as { hasActiveWorkflow?: boolean }).hasActiveWorkflow ?? false,
    hasActiveAgent: (t as { hasActiveAgent?: boolean }).hasActiveAgent ?? false,
    hasScheduledMessage:
      (t as { hasScheduledMessage?: boolean }).hasScheduledMessage ?? false,
    nextScheduledAt:
      (t as { nextScheduledAt?: string | null }).nextScheduledAt ?? null,
    pinnedAt: (t as { pinnedAt?: string | null }).pinnedAt ?? null,
    viewerIsParticipant: t.viewerIsParticipant,
  }));
}

export function mapGetToThreadDetail(data: ThreadGetPayload): ThreadDetail {
  return {
    id: data.id,
    title: data.title,
    status: data.status as "OPEN" | "RESOLVED" | "ARCHIVED",
    kind: data.kind as "CONVERSATION" | "INTERNAL" | "ESCALATION" | "BROADCAST",
    lastMessageAt: data.lastMessageAt,
    lastMessagePreview: data.lastMessagePreview,
    unreadCount: data.unreadCount,
    listIcon: data.listIcon ?? null,
    routingParticipantId: data.routingParticipantId ?? null,
    communicationChannelId:
      (data as { communicationChannelId?: string | null })
        .communicationChannelId ?? null,
    isWildcardRouting:
      (data as { isWildcardRouting?: boolean }).isWildcardRouting ?? false,
    routingPatternDisplay:
      (data as { routingPatternDisplay?: string | null })
        .routingPatternDisplay ?? null,
    routingAddress:
      (data as { routingAddress?: string | null }).routingAddress ?? null,
    isExclusiveFeed:
      (data as { isExclusiveFeed?: boolean }).isExclusiveFeed ?? false,
    recipientOptedOut:
      (data as { recipientOptedOut?: boolean }).recipientOptedOut ?? false,
    campaignBatchJobId:
      (data as { campaignBatchJobId?: string | null }).campaignBatchJobId ??
      null,
    campaignRootBatchJobId:
      (data as { campaignRootBatchJobId?: string | null })
        .campaignRootBatchJobId ?? null,
    participants: data.participants.map((p) => ({
      id: p.id,
      displayName: p.displayName,
      avatarUrl: p.avatarUrl,
      kind: p.kind as
        | "CONTACT"
        | "MEMBER"
        | "AGENT"
        | "SYSTEM"
        | "WORKFLOW"
        | "BATCH_JOB",
      role: p.role as "OWNER" | "PARTICIPANT" | "OBSERVER",
      joinedAt: p.joinedAt,
      memberId: p.memberId ?? null,
      contactId: (p as { contactId?: string | null }).contactId ?? null,
      agentId: (p as { agentId?: string | null }).agentId ?? null,
      workflowId: (p as { workflowId?: string | null }).workflowId ?? null,
      agentKind: toAgentKindValue(
        (p as { agentKind?: string | null }).agentKind,
      ),
      developerHref:
        (p as { developerHref?: string | null }).developerHref ?? null,
      instanceId: p.instanceId ?? null,
      instanceLabel: p.instanceLabel ?? null,
      associations: p.associations ?? [],
    })),
    parentId: data.parentId,
    parentThread: data.parentThread
      ? {
          id: data.parentThread.id,
          title: data.parentThread.title,
          linkedAt: data.parentThread.linkedAt,
          sourceMessageId: data.parentThread.sourceMessageId ?? null,
        }
      : null,
    referencedByThreads: data.referencedByThreads?.map((ref) => ({
      id: ref.id,
      title: ref.title,
      linkedAt: ref.linkedAt,
      sourceMessageId: ref.sourceMessageId ?? null,
    })),
    createdAt: data.createdAt,
    // Agent state is now tracked per-participant via ThreadParticipant.activeAgentRunId
    // and managed through Pusher events (thread.agent.started, thread.agent.completed)
    messages: (data.messages ?? []).map((m) => ({
      id: m.id,
      listStableKey: m.id,
      content: m.content,
      contentRaw: m.contentRaw ?? null,
      contentType: m.contentType as ThreadMessage["contentType"],
      createdAt: m.createdAt,
      sender: m.sender
        ? {
            id: m.sender.id,
            displayName: m.sender.displayName,
            avatarUrl: m.sender.avatarUrl,
            kind: m.sender.kind as
              | "CONTACT"
              | "MEMBER"
              | "AGENT"
              | "SYSTEM"
              | "WORKFLOW"
              | "BATCH_JOB",
            agentKind: toAgentKindValue(
              (m.sender as { agentKind?: string | null }).agentKind,
            ),
          }
        : {
            id: "system",
            displayName: "System",
            avatarUrl: null,
            kind: "SYSTEM" as const,
          },
      aiGenerated: m.aiGenerated,
      attachments: m.attachments.map((a) =>
        mapThreadMessageAttachment({
          id: a.id,
          fileId:
            "fileId" in a && typeof a.fileId === "string"
              ? a.fileId
              : undefined,
          name: a.name,
          mimeType: a.mimeType,
          url: a.url,
        }),
      ),
      agentTrace: m.agentTrace
        ? {
            agentRunId: m.agentTrace.agentRunId,
            agentId: m.agentTrace.agentId,
            agentName: m.agentTrace.agentName,
            trace: m.agentTrace.trace as AgentTrace["trace"],
          }
        : null,
    })),
    subThreads: data.subThreads?.map((st) => ({
      id: st.id,
      title: st.title,
      kind: st.kind as "CONVERSATION" | "INTERNAL" | "ESCALATION" | "BROADCAST",
      status: st.status as "OPEN" | "RESOLVED" | "ARCHIVED",
      parentId: st.parentId ?? null,
      messageCount: st.messageCount,
      lastMessagePreview: st.lastMessagePreview,
      lastMessageAt: st.lastMessageAt,
      participants: st.participants.map((p) => ({
        id: p.id,
        displayName: p.displayName,
        avatarUrl: p.avatarUrl,
        kind: p.kind as
          | "CONTACT"
          | "MEMBER"
          | "AGENT"
          | "SYSTEM"
          | "WORKFLOW"
          | "BATCH_JOB",
        role: p.role as "OWNER" | "PARTICIPANT" | "OBSERVER",
        joinedAt: p.joinedAt,
      })),
      unreadCount: st.unreadCount,
      createdAt: st.createdAt,
      sourceMessageId: st.sourceMessageId ?? null,
    })),
    workflowRuns: data.workflowRuns?.map((wr) => ({
      id: wr.id,
      handle: wr.handle,
      status: wr.status as ThreadWorkflowRunSummary["status"],
      startedAt: wr.startedAt,
      completedAt: wr.completedAt,
      workflowRunId: wr.workflowRunId,
      workflowName: wr.workflowName,
      stepsCompleted: wr.stepsCompleted,
      stepsTotal: wr.stepsTotal,
      sourceMessageId: wr.sourceMessageId ?? null,
    })),
    blocks: [],
  };
}

export function mapSendPayloadToMessage(
  msg: RouterOutputs["threads"]["sendMessage"],
  outboundStatus?: ThreadMessage["outboundStatus"],
): ThreadMessage {
  return {
    id: msg.id,
    listStableKey: msg.id,
    content: msg.content,
    contentType: msg.contentType as ThreadMessage["contentType"],
    createdAt: msg.createdAt,
    sender: msg.sender
      ? {
          id: msg.sender.id,
          displayName: msg.sender.displayName,
          avatarUrl: msg.sender.avatarUrl,
          kind: msg.sender.kind as
            | "CONTACT"
            | "MEMBER"
            | "AGENT"
            | "SYSTEM"
            | "WORKFLOW"
            | "BATCH_JOB",
        }
      : {
          id: "system",
          displayName: "System",
          avatarUrl: null,
          kind: "SYSTEM" as const,
        },
    aiGenerated: msg.aiGenerated,
    attachments: [],
    outboundStatus,
  };
}

export function parseThreadEnrichmentSseBlock(block: string): {
  deltaTitle?: string;
  terminal?: {
    skipped?: boolean;
    error?: boolean;
    title?: string;
    listIcon?: string | null;
  };
} | null {
  const trimmed = block.trim();
  if (!trimmed) return null;

  const dataLine = trimmed
    .split("\n")
    .map((l) => l.replace(/\r$/, ""))
    .find((l) => l.startsWith("data: "));

  if (!dataLine) return null;

  try {
    const json = JSON.parse(dataLine.slice(6)) as Record<string, unknown>;
    if (json.done === true) {
      if (json.skipped === true) {
        return { terminal: { skipped: true } };
      }
      if (json.error) {
        return { terminal: { error: true } };
      }
      return {
        terminal: {
          title: typeof json.title === "string" ? json.title : undefined,
          listIcon: typeof json.listIcon === "string" ? json.listIcon : null,
        },
      };
    }
    if (typeof json.title === "string") {
      return { deltaTitle: json.title };
    }
  } catch {
    return null;
  }
  return null;
}

export async function consumeThreadTitleStream(
  threadId: string,
  onDelta: (partialTitle: string) => void,
  onComplete: (payload: {
    skipped?: boolean;
    title?: string;
    listIcon?: string | null;
  }) => void,
): Promise<void> {
  const res = await fetch("/api/threads/stream-enrichment", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ threadId }),
  });

  if (!res.ok || !res.body) {
    onComplete({});
    return;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  const drainBuffer = (flushDecoder: boolean): boolean => {
    if (flushDecoder) {
      buffer += decoder.decode();
    }
    const blocks = buffer.split("\n\n");
    buffer = blocks.pop() ?? "";

    for (const block of blocks) {
      const parsed = parseThreadEnrichmentSseBlock(block);
      if (!parsed) continue;
      if (parsed.deltaTitle !== undefined) {
        onDelta(parsed.deltaTitle);
      }
      if (parsed.terminal) {
        const t = parsed.terminal;
        if (t.skipped) {
          onComplete({ skipped: true });
          return true;
        }
        if (t.error) {
          onComplete({});
          return true;
        }
        onComplete({
          title: t.title,
          listIcon: t.listIcon ?? null,
        });
        return true;
      }
    }
    return false;
  };

  try {
    while (true) {
      const { done, value } = await reader.read();

      if (value) {
        buffer += decoder.decode(value, { stream: true });
      }

      if (drainBuffer(false)) {
        return;
      }

      if (done) {
        if (drainBuffer(true)) {
          return;
        }
        const tail = buffer.trim();
        if (tail) {
          const parsed = parseThreadEnrichmentSseBlock(tail);
          if (parsed?.deltaTitle !== undefined) {
            onDelta(parsed.deltaTitle);
          }
          if (parsed?.terminal) {
            const t = parsed.terminal;
            if (t.skipped) {
              onComplete({ skipped: true });
              return;
            }
            if (t.error) {
              onComplete({});
              return;
            }
            onComplete({
              title: t.title,
              listIcon: t.listIcon ?? null,
            });
            return;
          }
        }
        break;
      }
    }
  } finally {
    reader.releaseLock();
  }

  onComplete({});
}
